/* MAX AUTO mobile PDP v1.6 — mobile touch swipes alongside the original tab controls. */
(() => {
  'use strict';
  const NAME = 'maxauto-mobile-product-tabs';
  if (customElements.get(NAME)) return;
  const KEYS = ['overview', 'specs', 'features', 'condition'];
  const T = (k, f) => (window.maxT ? window.maxT(k, f) : f);
  const LABELS = [T('pdp.tab_overview', 'Overview'), T('pdp.tab_specs', 'Specs'), T('pdp.tab_features', 'Features'), T('pdp.tab_condition', 'Condition')];
  const IGNORE = 'script, style, link, template';

  class MobileProductTabs extends HTMLElement {
    connectedCallback() {
      this.start = () => this.init();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.start, { once: true });
      else this.init();
    }
    init() {
      if (this.root || !this.isConnected) return;
      const root = document.getElementById(`ProductPage-${this.dataset.sectionId}`);
      if (!root || root.dataset.maxPdpOwner) return;
      const info = root.querySelector('.product-info-wrap-content');
      const description = root.querySelector('.product-info-description');
      if (!info || !description || !root.querySelector('[id^="Specifications-"]') || !root.querySelector('[id^="OptionInfo-"]')) {
        console.warn('[MAX mobile tabs] Unsupported product markup; original page retained.');
        return;
      }
      this.root = root;
      this.info = info;
      this.description = description;
      this.spacing = root.closest('.block-section');
      this.spacing?.setAttribute('data-max-pdp-section-spacing', '');
      this.current = 'overview';
      this.accordions = new Map();
      this.desktopDefaults = new Map();
      this.notices = new Map();
      this.pageFooters = new Set();
      this.layerStyles = new Map();
      this.hideAvailability();
      this.markPageFooter();
      this.applyDesktopDefaults();
      this.originalAttributes = new Map(['role', 'aria-labelledby', 'tabindex'].map(a => [a, root.getAttribute(a)]));
      root.dataset.maxPdpOwner = this.dataset.sectionId;
      this.nav = document.createElement('div');
      this.nav.dataset.maxPdpNav = '';
      this.nav.setAttribute('role', 'tablist');
      this.nav.setAttribute('aria-label', T('pdp.tab_nav_aria', 'Vehicle details'));
      this.buttons = KEYS.map((key, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.id = `max-pdp-${this.dataset.sectionId}-${key}`;
        button.className = 'max-pdp-tab';
        button.textContent = LABELS[index];
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', root.id);
        button.addEventListener('click', () => this.select(key, true));
        button.addEventListener('keydown', e => {
          let next;
          if (e.key === 'ArrowRight') next = (index + 1) % 4;
          if (e.key === 'ArrowLeft') next = (index + 3) % 4;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = 3;
          if (next !== undefined) { e.preventDefault(); this.buttons[next].focus(); this.select(KEYS[next], true); }
        });
        this.nav.append(button);
        return button;
      });
      root.before(this.nav);
      this.dock = document.createElement('div');
      this.dock.dataset.maxPdpDock = '';
      this.dock.setAttribute('aria-hidden', 'true');
      this.spacer = document.createElement('div');
      this.spacer.dataset.maxPdpSpacer = '';
      this.spacer.setAttribute('aria-hidden', 'true');
      root.after(this.spacer, this.dock);
      this.bindSwipe();
      this.media = matchMedia('(max-width: 749px)');
      this.onMedia = () => this.media.matches ? this.enable() : this.disable();
      this.media.addEventListener('change', this.onMedia);
      this.onMedia();
      this.observer = new MutationObserver(() => {
        if (this.refreshFrame) return;
        this.refreshFrame = requestAnimationFrame(() => {
          this.refreshFrame = 0;
          this.applyDesktopDefaults();
          if (!this.active) return;
          this.classify();
          this.configureActions();
          this.mobileAccordionDefaults();
          this.select(this.current);
        });
      });
      this.observer.observe(root, { childList: true, subtree: true });
      this.pageObserver = new MutationObserver(records => {
        if (records.some(r => r.type === 'attributes' || [...r.addedNodes].some(n => n.nodeType === 1))) {
          this.hideAvailability();
          this.markPageFooter();
        }
      });
      this.pageObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
      this.resizeObserver = new ResizeObserver(() => this.measureActions());
      this.resizeObserver.observe(root);
      this.onViewport = () => {
        const viewport = window.visualViewport;
        const editing = document.activeElement?.matches('input:not([type=hidden]), textarea, select, [contenteditable=true]');
        document.documentElement.classList.toggle('max-pdp-keyboard', Boolean(this.active && editing && viewport && innerHeight - viewport.height > 150));
      };
      window.visualViewport?.addEventListener('resize', this.onViewport);
      document.addEventListener('focusout', this.onViewport);
      this.onBlockSelect = e => {
        if (!this.active || !root.contains(e.target)) return;
        const group = e.target.closest('[data-max-pdp-group]');
        if (group) this.select(group.dataset.maxPdpGroup, true);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.onHash = () => {
        let target;
        try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch (_) { return; }
        if (!this.active || !target || !root.contains(target)) return;
        const group = target.closest('[data-max-pdp-group]');
        if (group) this.select(group.dataset.maxPdpGroup, true);
      };
      window.addEventListener('hashchange', this.onHash);
      this.onHash();
    }
    bindSwipe() {
      // Never take a gesture away from galleries, controls, or nested horizontal scrollers.
      const excluded = 'a, button, input, select, textarea, label, summary, video, audio, iframe, [contenteditable]:not([contenteditable="false"]), [draggable="true"], [role="slider"], [role="combobox"], [data-max-pdp-no-swipe], .product-media-gallery-wrap, media-gallery, .flickity-enabled, .swiper, [aria-roledescription="carousel"]';
      const ignore = target => {
        if (!(target instanceof Element) || target.closest(excluded)) return true;
        for (let node = target; node && node !== this.root; node = node.parentElement) {
          if (node.scrollWidth > node.clientWidth + 4 && /auto|scroll/.test(getComputedStyle(node).overflowX)) return true;
        }
        return false;
      };
      const reset = () => { this.swipe = null; };
      this.onTouchStart = event => {
        reset();
        if (!this.active || event.touches.length !== 1 || ignore(event.target) || window.getSelection()?.toString()) return;
        const touch = event.touches[0];
        // Leave native browser edge-back/forward gestures alone.
        if (touch.clientX < 24 || touch.clientX > document.documentElement.clientWidth - 24) return;
        this.swipe = { id: touch.identifier, x: touch.clientX, y: touch.clientY, time: performance.now(), tab: this.current, horizontal: false };
      };
      this.onTouchMove = event => {
        const gesture = this.swipe;
        if (!gesture) return;
        if (!this.active || event.touches.length !== 1) { reset(); return; }
        const touch = [...event.touches].find(t => t.identifier === gesture.id);
        if (!touch) { reset(); return; }
        const dx = Math.abs(touch.clientX - gesture.x), dy = Math.abs(touch.clientY - gesture.y);
        if (Math.max(dx, dy) < 12) return;
        // Once a gesture starts vertically/diagonally, it remains a page scroll.
        if (dx < dy * 1.5 || !event.cancelable) { reset(); return; }
        gesture.horizontal = true;
        event.preventDefault();
      };
      this.onTouchEnd = event => {
        const gesture = this.swipe;
        reset();
        if (!this.active || !gesture?.horizontal || event.touches.length || gesture.tab !== this.current || performance.now() - gesture.time > 900) return;
        const touch = [...event.changedTouches].find(t => t.identifier === gesture.id);
        if (!touch) return;
        const dx = touch.clientX - gesture.x, dy = touch.clientY - gesture.y;
        const distance = Math.max(48, Math.min(80, this.root.clientWidth * .16));
        if (Math.abs(dx) < distance || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        if (event.cancelable) event.preventDefault();
        this.swipeClickUntil = performance.now() + 400;
        const next = KEYS.indexOf(this.current) + (dx < 0 ? 1 : -1);
        if (next >= 0 && next < KEYS.length) this.select(KEYS[next], true);
      };
      this.onTouchCancel = reset;
      this.onSwipeClick = event => {
        // Suppress only the compatibility click following a swipe, not keyboard activation.
        if (event.detail && performance.now() < this.swipeClickUntil) {
          event.preventDefault(); event.stopImmediatePropagation();
        }
      };
      this.root.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.root.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.root.addEventListener('touchend', this.onTouchEnd, { passive: false });
      this.root.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
      this.root.addEventListener('click', this.onSwipeClick, true);
    }
    hideAvailability() {
      document.querySelectorAll('.maxauto-availability, [data-maxauto-availability]').forEach(node => {
        if (!this.notices.has(node)) this.notices.set(node, { value: node.style.getPropertyValue('display'), priority: node.style.getPropertyPriority('display') });
        const meaningful = ['unavailable', 'in_progress'].includes(node.dataset.state);
        const style = this.notices.get(node);
        if (meaningful) {
          if (style.value) node.style.setProperty('display', style.value, style.priority);
          else node.style.removeProperty('display');
        } else node.style.setProperty('display', 'none', 'important');
      });
    }
    markPageFooter() {
      document.querySelectorAll('footer, [role="contentinfo"], [id^="MaxFooter-"]').forEach(node => {
        if (node.closest('main, dialog, [role="dialog"]')) return;
        const target = node.closest('.shopify-section-group-footer-group') || node.closest('.shopify-section') || node;
        if (this.pageFooters.has(target)) return;
        target.setAttribute('data-max-pdp-page-footer', '');
        this.pageFooters.add(target);
      });
    }
    classify() {
      const mark = (node, group) => { node.dataset.maxPdpGroup = group; };
      [...this.root.children].forEach(node => {
        if (node !== this.info && node !== this.description && !node.matches(IGNORE)) mark(node, 'overview');
      });
      const content = this.description.querySelector('.product-info-description-content') || this.description;
      [...content.children].filter(node => !node.matches(IGNORE)).forEach(node => {
        let group = 'specs'; // Unknown extra details remain reachable; never silently drop content.
        // Availability can live outside the price block (including in a theme wrapper).
        // Treat a dedicated status wrapper as Overview, never as Specifications.
        const availability = '.maxauto-availability, [data-maxauto-availability]';
        const detailCards = '[id^="Specifications-"], [id^="Description-"], [id^="OptionInfo-"], [id^="InsuranceHistory-"], [id^="ConditionReport-"], max-inspection-diagram';
        if (node.matches(availability) || (node.querySelector(availability) && !node.matches(detailCards) && !node.querySelector(detailCards))) group = 'overview';
        if (node.matches('[id^="OptionInfo-"]') || node.querySelector('[id^="OptionInfo-"]')) group = 'features';
        if (node.matches('max-inspection-diagram, [id^="InsuranceHistory-"], [id^="ConditionReport-"]') || node.querySelector('max-inspection-diagram, [id^="InsuranceHistory-"], [id^="ConditionReport-"]')) group = 'condition';
        mark(node, group);
      });
      this.root.querySelectorAll('.maxauto-availability, [data-maxauto-availability]').forEach(node => {
        if (!this.info.contains(node)) mark(node, 'overview');
      });
    }
    configureActions() {
      const quote = this.info.querySelector('[data-request-quotation]');
      const actions = this.info.querySelector('.product-actions-add-to-cart');
      const supported = quote || actions?.querySelector('button[name="add"], .btn-whatsapp');
      this.hasDock = Boolean(supported);
      // No synthesized purchase controls on sold/catalog-only or structurally different products.
      if (!supported) {
        this.info.querySelectorAll('.max-pdp-action-path, .max-pdp-quote, .max-pdp-actions, .max-pdp-error').forEach(n => n.classList.remove('max-pdp-action-path', 'max-pdp-quote', 'max-pdp-actions', 'max-pdp-error'));
        this.info.classList.remove('max-pdp-action-path');
        this.dock.classList.remove('max-pdp-on');
        this.spacer.classList.remove('max-pdp-on');
        return;
      }
      this.quote = quote;
      this.actions = actions;
      quote?.classList.add('max-pdp-quote');
      actions?.classList.add('max-pdp-actions');
      const statuses = [...this.info.querySelectorAll('.maxauto-availability, [data-maxauto-availability]')];
      statuses.forEach(node => node.classList.add('max-pdp-status'));
      [quote, actions, ...statuses].filter(Boolean).forEach(node => {
        for (let parent = node.parentElement; parent && parent !== this.root; parent = parent.parentElement) {
          parent.classList.add('max-pdp-action-path');
        }
      });
      this.info.querySelectorAll('.product-form-error-message-wrapper').forEach(node => node.classList.add('max-pdp-error'));
      this.setActionDock();
      if (this.resizeObserver) [quote, actions].filter(Boolean).forEach(node => this.resizeObserver.observe(node));
      this.measureActions();
    }
    measureActions() {
      if (!this.active || !this.hasDock || this.current === 'overview') return;
      const height = Math.ceil(this.actions?.getBoundingClientRect().height || 0);
      const quoteHeight = Math.ceil(this.quote?.getBoundingClientRect().height || 0);
      // Avoid ResizeObserver feedback loops and never replace a measurement with hidden zero.
      if (height > 0 && height !== this.actionHeight) {
        this.actionHeight = height;
        document.documentElement.style.setProperty('--max-pdp-actions-height', `${height}px`);
      }
      if (quoteHeight > 0 && quoteHeight !== this.quoteHeight) {
        this.quoteHeight = quoteHeight;
        document.documentElement.style.setProperty('--max-pdp-quote-height', `${quoteHeight}px`);
      }
    }
    setActionDock() {
      const docked = this.active && this.hasDock && this.current !== 'overview';
      // v1.4: no viewport dock or compensating spacer; CSS orders the original info
      // wrapper after the selected details in normal flow (mobile non-Overview only).
      this.dock.classList.remove('max-pdp-on');
      this.spacer.classList.remove('max-pdp-on');
      if (docked) {
        this.root.querySelectorAll('.max-pdp-action-path').forEach(node => {
          if (!this.layerStyles.has(node)) this.layerStyles.set(node, { value: node.style.getPropertyValue('z-index'), priority: node.style.getPropertyPriority('z-index') });
          node.style.setProperty('z-index', 'auto', 'important');
        });
      } else {
        this.layerStyles.forEach((style, node) => style.value ? node.style.setProperty('z-index', style.value, style.priority) : node.style.removeProperty('z-index'));
        this.layerStyles.clear();
      }
    }
    mobileAccordionDefaults() {
      this.root.querySelectorAll('max-inspection-diagram details').forEach(details => {
        if (this.accordions.has(details)) return;
        this.accordions.set(details, { open: details.open });
        details.open = true;
      });
      this.root.querySelectorAll('[id^="OptionInfo-"], [id^="InsuranceHistory-"], [id^="ConditionReport-"]').forEach(card => {
        const button = card.querySelector('.ih-toggle');
        const body = card.querySelector('.ih-body');
        if (!button || !body || this.accordions.has(button)) return;
        this.accordions.set(button, { body, expanded: button.getAttribute('aria-expanded'), isOpen: button.classList.contains('is-open'), hidden: body.hidden });
        const open = card.id.startsWith('OptionInfo-');
        button.setAttribute('aria-expanded', String(open));
        button.classList.toggle('is-open', open);
        body.hidden = !open;
      });
    }
    applyDesktopDefaults() {
      this.root.querySelectorAll('[id^="OptionInfo-"], [id^="InsuranceHistory-"], [id^="ConditionReport-"]').forEach(card => {
        const button = card.querySelector('.ih-toggle');
        const body = card.querySelector('.ih-body');
        if (!button || !body || this.desktopDefaults.has(button)) return;
        this.desktopDefaults.set(button, { body, expanded: button.getAttribute('aria-expanded'), isOpen: button.classList.contains('is-open'), hidden: body.hidden });
        button.setAttribute('aria-expanded', 'false');
        button.classList.remove('is-open');
        body.hidden = true;
      });
    }
    enable() {
      if (this.active) return;
      this.active = true;
      document.documentElement.classList.add('max-pdp-mobile');
      this.root.classList.add('max-pdp-active');
      this.root.setAttribute('role', 'tabpanel');
      this.root.setAttribute('tabindex', '0');
      this.nav.classList.add('max-pdp-on');
      this.classify();
      this.configureActions();
      this.mobileAccordionDefaults();
      this.select(this.current);
    }
    select(key, userAction = false) {
      if (!this.active || !KEYS.includes(key)) return;
      const changed = key !== this.current;
      this.current = key;
      this.root.dataset.maxPdpTab = key;
      this.setActionDock();
      this.buttons.forEach((button, i) => {
        button.setAttribute('aria-selected', String(KEYS[i] === key));
        button.tabIndex = KEYS[i] === key ? 0 : -1;
      });
      const activeButton = this.buttons[KEYS.indexOf(key)];
      this.root.setAttribute('aria-labelledby', activeButton.id);
      if (changed || userAction) {
        const rail = this.nav.getBoundingClientRect();
        const tab = activeButton.getBoundingClientRect();
        const offset = tab.left < rail.left ? tab.left - rail.left : tab.right > rail.right ? tab.right - rail.right : 0;
        if (offset) this.nav.scrollBy({ left: offset, behavior: 'instant' });
      }
      this.root.querySelectorAll('[data-max-pdp-group]').forEach(node => node.classList.toggle('max-pdp-hidden', node.dataset.maxPdpGroup !== key));
      const overviewDetail = this.description.querySelector('[data-max-pdp-group="overview"]');
      this.description.classList.toggle('max-pdp-hidden', key === 'overview' && !overviewDetail);
      this.info.classList.toggle('max-pdp-hidden', !this.hasDock && key !== 'overview');
      if (changed) {
        this.swipe = null;
        // Pause local video without resetting it; don't reload third-party iframe players.
        this.root.querySelectorAll('.max-pdp-hidden video').forEach(video => video.pause());
        // Notify existing responsive galleries after their display:none panel becomes visible.
        requestAnimationFrame(() => { if (this.active) window.dispatchEvent(new Event('resize')); });
      }
      if (userAction && this.nav.getBoundingClientRect().top < 0) this.nav.scrollIntoView({ block: 'start', behavior: 'instant' });
      // Sticky nav's rect is zero when stuck; scroll using its normal-flow anchor instead.
      else if (userAction) {
        const top = this.root.getBoundingClientRect().top - this.nav.offsetHeight + window.scrollY;
        if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
      }
      this.measureActions();
    }
    disable() {
      if (!this.active) return;
      this.active = false;
      this.swipe = null;
      this.swipeClickUntil = 0;
      this.root.classList.remove('max-pdp-active');
      this.root.removeAttribute('data-max-pdp-tab');
      this.root.querySelectorAll('.max-pdp-hidden').forEach(n => n.classList.remove('max-pdp-hidden'));
      this.originalAttributes.forEach((value, key) => value === null ? this.root.removeAttribute(key) : this.root.setAttribute(key, value));
      [this.nav, this.dock, this.spacer].forEach(n => n.classList.remove('max-pdp-on'));
      this.accordions.forEach((state, node) => {
        if ('open' in state) node.open = state.open;
        else {
          if (state.expanded === null) node.removeAttribute('aria-expanded'); else node.setAttribute('aria-expanded', state.expanded);
          node.classList.toggle('is-open', state.isOpen);
          state.body.hidden = state.hidden;
        }
      });
      this.accordions.clear();
      this.layerStyles.forEach((style, node) => style.value ? node.style.setProperty('z-index', style.value, style.priority) : node.style.removeProperty('z-index'));
      this.layerStyles.clear();
      document.documentElement.classList.remove('max-pdp-mobile', 'max-pdp-keyboard');
      ['--max-pdp-actions-height', '--max-pdp-quote-height'].forEach(key => document.documentElement.style.removeProperty(key));
      this.actionHeight = this.quoteHeight = 0;
    }
    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.start);
      if (!this.root) return;
      this.disable();
      this.desktopDefaults.forEach((state, node) => {
        if (state.expanded === null) node.removeAttribute('aria-expanded'); else node.setAttribute('aria-expanded', state.expanded);
        node.classList.toggle('is-open', state.isOpen);
        state.body.hidden = state.hidden;
      });
      this.observer?.disconnect();
      this.root.removeEventListener('touchstart', this.onTouchStart);
      this.root.removeEventListener('touchmove', this.onTouchMove);
      this.root.removeEventListener('touchend', this.onTouchEnd);
      this.root.removeEventListener('touchcancel', this.onTouchCancel);
      this.root.removeEventListener('click', this.onSwipeClick, true);
      this.pageObserver?.disconnect();
      this.notices.forEach((style, node) => style.value ? node.style.setProperty('display', style.value, style.priority) : node.style.removeProperty('display'));
      this.pageFooters.forEach(node => node.removeAttribute('data-max-pdp-page-footer'));
      this.spacing?.removeAttribute('data-max-pdp-section-spacing');
      this.root.querySelectorAll('.max-pdp-status').forEach(node => node.classList.remove('max-pdp-status'));
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.refreshFrame);
      this.media?.removeEventListener('change', this.onMedia);
      window.visualViewport?.removeEventListener('resize', this.onViewport);
      document.removeEventListener('focusout', this.onViewport);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('hashchange', this.onHash);
      [this.nav, this.dock, this.spacer].forEach(n => n.remove());
      this.root.querySelectorAll('[data-max-pdp-group]').forEach(n => n.removeAttribute('data-max-pdp-group'));
      this.root.querySelectorAll('.max-pdp-action-path, .max-pdp-quote, .max-pdp-actions, .max-pdp-error').forEach(n => n.classList.remove('max-pdp-action-path', 'max-pdp-quote', 'max-pdp-actions', 'max-pdp-error'));
      delete this.root.dataset.maxPdpOwner;
      this.root = null;
    }
  }
  customElements.define(NAME, MobileProductTabs);
})();
